// Central sink for client-side crashes caught by ErrorBoundary (finding U-24).
// Forwards the error to any available monitoring SDK (App Insights / Sentry)
// and POSTs a structured payload to the backend /api/clienterror endpoint.
//
// Design rules:
// - Logging must NEVER throw. Every transport is individually guarded so a
//   failure in one path can't mask the original crash or break the fallback UI.
// - Uses navigator.sendBeacon when available (reliable, fire-and-forget, works
//   during page unload) and falls back to fetch with keepalive. We deliberately
//   avoid the shared axios instance so the 401/403 response interceptor in
//   baseApi.js can't trigger a redirect loop while we are already in an error state.

export const CLIENT_ERROR_ENDPOINT =
  process.env.REACT_APP_CLIENT_ERROR_ENDPOINT || "/api/clienterror";

/**
 * Build the JSON-serialisable payload sent to the backend.
 * @param {Error} error
 * @param {{ componentStack?: string }} [errorInfo]
 * @param {object} [context] extra fields (e.g. route, userId)
 */
export function buildErrorPayload(error, errorInfo, context = {}) {
  const safeError = error || {};
  let message = safeError.message;
  if (!message) {
    message = error == null ? "Unknown error" : String(error) || "Unknown error";
  }
  return {
    message,
    name: safeError.name || "Error",
    stack: safeError.stack || null,
    componentStack: (errorInfo && errorInfo.componentStack) || null,
    url:
      typeof window !== "undefined" && window.location
        ? window.location.href
        : null,
    userAgent:
      typeof navigator !== "undefined" ? navigator.userAgent : null,
    timestamp: new Date().toISOString(),
    ...context,
  };
}

function forwardToMonitoringSdks(error, payload) {
  if (typeof window === "undefined") return;

  // Azure Application Insights (if the SDK is loaded on the page).
  try {
    if (window.appInsights && typeof window.appInsights.trackException === "function") {
      window.appInsights.trackException({ exception: error, properties: payload });
    }
  } catch (_) {
    /* swallow: monitoring must never break the app */
  }

  // Sentry (if the SDK is loaded on the page).
  try {
    if (window.Sentry && typeof window.Sentry.captureException === "function") {
      window.Sentry.captureException(error, { extra: payload });
    }
  } catch (_) {
    /* swallow */
  }
}

function sendToBackend(payload) {
  const body = JSON.stringify(payload);

  // Preferred: sendBeacon (non-blocking, survives unload).
  try {
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.sendBeacon === "function"
    ) {
      const blob =
        typeof Blob !== "undefined"
          ? new Blob([body], { type: "application/json" })
          : body;
      const queued = navigator.sendBeacon(CLIENT_ERROR_ENDPOINT, blob);
      if (queued) return;
    }
  } catch (_) {
    /* fall through to fetch */
  }

  // Fallback: fetch with keepalive.
  try {
    if (typeof fetch === "function") {
      fetch(CLIENT_ERROR_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        credentials: "include",
        keepalive: true,
      }).catch(() => {});
    }
  } catch (_) {
    /* swallow: nothing more we can do */
  }
}

/**
 * Report a client-side error. Safe to call from anywhere; never throws.
 * @param {Error} error
 * @param {{ componentStack?: string }} [errorInfo]
 * @param {object} [context]
 */
export function logClientError(error, errorInfo, context) {
  try {
    const payload = buildErrorPayload(error, errorInfo, context);
    forwardToMonitoringSdks(error, payload);
    sendToBackend(payload);
    return payload;
  } catch (_) {
    // Absolute last-resort guard. Logging the logger failure to the console is
    // acceptable because it only runs if our own guards above failed.
    try {
      // eslint-disable-next-line no-console
      console.error("clientErrorLogger failed to report an error", _);
    } catch (__) {
      /* give up silently */
    }
    return null;
  }
}

export default logClientError;
